import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "../layout/Layout";
import SEO from "../Components/SEO/SEO";
import { Button } from "../Components/ui/Button";
import { services } from "../data/siteData";
import {
  Layout as LayoutIcon,
  Globe,
  Smartphone,
  Cpu,
  Database,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Clock,
  Users,
  MessageSquare,
  Code,
} from "lucide-react";

const iconMap = {
  Layout: LayoutIcon,
  Globe,
  Smartphone,
  Cpu,
  Database,
  ShieldCheck,
};

const gradients = [
  "from-blue-500/10",
  "from-purple-500/10",
  "from-pink-500/10",
  "from-emerald-500/10",
  "from-amber-500/10",
  "from-indigo-500/10",
];

function serviceSlug(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

const engagementSteps = [
  {
    icon: MessageSquare,
    title: "Discovery call",
    desc: "We clarify goals, users, constraints, and what success looks like before scoping.",
  },
  {
    icon: Code,
    title: "Scoped proposal",
    desc: "Fixed deliverables, timeline, and pricing — no open-ended hourly surprises.",
  },
  {
    icon: Users,
    title: "Senior-led build",
    desc: "Architecture and quality stay with leadership; delivery support keeps sprints moving.",
  },
  {
    icon: Clock,
    title: "Launch & handoff",
    desc: "QA, deployment support, documentation, and a post-launch window for fixes.",
  },
];

export default function ServicesPage() {
  return (
    <Layout>
      <SEO
        title="Services | Quantum Spark Technology"
        description="UI/UX, web and mobile development, AI, blockchain, and QA services from Quantum Spark Technology."
        path="/services"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#FAFAFB] pt-12 pb-20 md:pt-16 md:pb-28">
        <div className="pointer-events-none absolute top-28 right-0 h-[600px] w-[600px] translate-x-1/4 rounded-full bg-[#6F00FF]/5 blur-[120px]" />
        <div className="pointer-events-none absolute bottom-0 left-0 h-[400px] w-[400px] -translate-x-1/4 translate-y-1/3 rounded-full bg-blue-500/5 blur-[100px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-[#6F00FF]">Services</p>
            <h1 className="mb-6 font-display text-4xl font-extrabold tracking-tight text-[#0B0B16] md:text-6xl">
              Everything you need to{" "}
              <span className="bg-gradient-to-r from-[#6F00FF] to-[#9333EA] bg-clip-text text-transparent">
                ship.
              </span>
            </h1>
            <p className="mb-10 max-w-2xl text-lg leading-relaxed text-[#5A5A6E]">
              From first wireframe to production launch — UI/UX, web, mobile, AI, blockchain, and QA under one
              senior-led team. Every engagement starts with discovery so scope and ownership are clear upfront.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Link to="/#contact">
                <Button className="h-14 rounded-full bg-[#6F00FF] px-8 text-base font-semibold text-white shadow-[0_8px_30px_rgb(111,0,255,0.2)] hover:bg-[#5D21D2]">
                  Start a project <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#capabilities">
                <Button
                  variant="outline"
                  className="h-14 rounded-full border-zinc-200 bg-white px-8 text-base font-semibold text-[#0B0B16] hover:bg-zinc-50"
                >
                  Browse capabilities
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="border-y border-zinc-200 bg-white py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 max-w-2xl">
            <h2 className="mb-4 font-display text-3xl font-bold text-[#0B0B16] md:text-4xl">Our capabilities</h2>
            <p className="text-lg text-[#5A5A6E]">
              Six core disciplines we combine on every engagement — pick one or stack them for a full product build.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {services.map((service, i) => {
              const Icon = iconMap[service.icon] || LayoutIcon;
              const slug = serviceSlug(service.title);

              return (
                <motion.article
                  key={service.title}
                  id={slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.05 }}
                  className="group flex scroll-mt-28 flex-col overflow-hidden rounded-3xl border border-zinc-200 bg-[#FAFAFB] transition-all hover:border-[#6F00FF]/30 hover:shadow-[0_0_40px_-10px_rgb(111,0,255,0.12)]"
                >
                  <div className={`relative h-24 bg-gradient-to-b ${gradients[i]} to-transparent`}>
                    <div className="absolute -bottom-6 left-8 flex h-16 w-16 items-center justify-center rounded-2xl border border-zinc-200 bg-white text-[#6F00FF] shadow-sm transition-transform group-hover:scale-110">
                      <Icon size={24} />
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col px-8 pb-8 pt-10">
                    <h3 className="mb-2 font-display text-2xl font-bold text-[#0B0B16]">{service.title}</h3>
                    <p className="mb-4 font-medium text-[#6F00FF]">{service.shortDesc}</p>
                    <p className="mb-6 flex-1 text-sm leading-relaxed text-[#5A5A6E]">{service.detail}</p>

                    <ul className="mb-6 grid gap-2 sm:grid-cols-2">
                      {service.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                          <CheckCircle2 size={14} className="shrink-0 text-[#6F00FF]" />
                          {bullet}
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/#contact"
                      className="inline-flex items-center text-sm font-semibold text-[#6F00FF] transition-all group-hover:gap-2"
                    >
                      Discuss this service <ArrowRight size={16} className="ml-1" />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* How we engage */}
      <section className="bg-[#FAFAFB] py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold text-[#0B0B16] md:text-4xl">How every engagement starts</h2>
            <p className="mx-auto max-w-2xl text-lg text-[#5A5A6E]">
              No mystery scopes. We align on outcomes first, then build with weekly demos and transparent progress.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {engagementSteps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-zinc-200 bg-[#FAFAFB] text-[#6F00FF]">
                  <step.icon size={20} />
                </div>
                <p className="mb-1 text-xs font-bold uppercase tracking-widest text-zinc-400">Step {i + 1}</p>
                <h3 className="mb-2 font-display text-lg font-bold text-[#0B0B16]">{step.title}</h3>
                <p className="text-sm leading-relaxed text-[#5A5A6E]">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-6 mb-6 overflow-hidden rounded-[3rem] bg-[#6F00FF] py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-4 font-display text-3xl font-bold text-white md:text-4xl">Ready to scope your build?</h2>
          <p className="mb-8 text-lg text-white/80">
            Tell us what you&apos;re building. We&apos;ll respond within 24 hours with next steps for a discovery call.
          </p>
          <Link to="/#contact">
            <Button className="h-14 rounded-full bg-white px-8 text-base font-bold text-[#6F00FF] hover:bg-zinc-100">
              Book a free discovery call
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
